import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { fmtBRL } from "@/lib/format";

const CUR_MONTH = new Date().toISOString().slice(0, 7);

export function GoalFormModal({ open, onOpenChange, goal, salespeople, defaultMonth, onSubmit, submitting }) {
  const isEdit = Boolean(goal);
  const [form, setForm] = useState({ salesperson_id: "", month: defaultMonth || CUR_MONTH, target_value: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm(
        goal
          ? { salesperson_id: goal.salesperson_id, month: goal.month, target_value: String(goal.meta) }
          : { salesperson_id: "", month: defaultMonth || CUR_MONTH, target_value: "" }
      );
    }
  }, [open, goal, defaultMonth]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.salesperson_id) return setError("Selecione o vendedor.");
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(form.month)) return setError("Mês inválido.");
    const t = parseFloat(form.target_value);
    if (!(t > 0)) return setError("Meta deve ser maior que zero.");
    try {
      await onSubmit({ salesperson_id: form.salesperson_id, month: form.month, target_value: t });
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível salvar a meta.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="goal-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Editar meta" : "Nova meta"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div data-testid="goal-form-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {typeof error === "string" ? error : "Erro de validação."}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Vendedor *</Label>
            <Select value={form.salesperson_id} onValueChange={set("salesperson_id")}>
              <SelectTrigger data-testid="goal-salesperson-select">
                <SelectValue placeholder="Selecione o vendedor" />
              </SelectTrigger>
              <SelectContent>
                {salespeople.map((sp) => (
                  <SelectItem key={sp.salesperson_id} value={sp.salesperson_id}>{sp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="goal-month">Mês/Ano *</Label>
              <Input id="goal-month" type="month" value={form.month} onChange={set("month")} data-testid="goal-month-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="goal-target">Meta (R$) *</Label>
              <Input id="goal-target" type="number" step="0.01" min="0" value={form.target_value} onChange={set("target_value")} data-testid="goal-target-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Período: mensal · Valor: {fmtBRL(parseFloat(form.target_value) || 0)}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="goal-submit-button" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Salvar" : "Criar meta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
